import { DocumentCard } from "views/applications-id-report/slices/IncomeReportSlice/components/DocumentCard";
import { BankAccountDetailsSlice } from "./BankAccountDetailsSlice";
import { parseDocStatus } from "views/applications-id-report/utils/parseDocStatus";
import { WarningRecommendation } from "../../WarningRecommendation";
import { Show } from "shared/components";
import { getBankStatementReasonCodes } from "../utils";
import { IncomeSummarySlice } from "./IncomeSummarySlice";
import { SimpleTable } from "../../PaystubTable/components/SimpleTable";
import { useMemo, useState } from "react";
import { cx, formatToCurrency } from "shared/utils";
import moment from "moment";
import { BankStatementTableRowData } from "../types";
import {
  BankStatementItem,
  BankStatementItemData
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/bank-statment";
import { DepositTabs } from "./DepositTabs";

export const ExpandedTableRow = ({
  rowData
}: {
  rowData: BankStatementTableRowData;
}) => {
  const [activeTab, setActiveTab] = useState<"CompanyDeposits" | "AllDeposits">(
    "CompanyDeposits"
  );

  const { docScore, status, reasonCodes, hasAsset } = useMemo(() => {
    const docScore = rowData.raw_data?.trust_score ?? 0;

    return {
      docScore,
      status: parseDocStatus(docScore) as "Rejected" | "Warning" | "Accepted",
      reasonCodes: getBankStatementReasonCodes(rowData),
      hasAsset: Boolean(rowData.asset?.asset)
    };
  }, [rowData]);

  const {
    bank_accounts: bankAccounts,
    name,
    address
  } = rowData.raw_data?.parsed_details || {};

  return (
    <>
      <WarningRecommendation
        status={status}
        rejectionReason={rowData.additional_data?.risk_assessment_summary}
        score={docScore}
        additionalData={{ filename: rowData.asset?.asset?.filename }}
      />

      <Show if={hasAsset}>
        <div className="h-[1px] w-full bg-alpha-40 my-3" />
        <DocumentCard
          score={docScore}
          asset={rowData.asset?.asset}
          reasonCodes={reasonCodes}
          ignorePrint
        />
      </Show>

      <BankAccountDetailsSlice
        bankAccount={bankAccounts?.[0]}
        holderName={name?.variants[0] || name?.raw}
        address={address}
      />
      <IncomeSummarySlice bankAccount={bankAccounts?.[0]} />

      <DepositTabs
        companyName={rowData.companyName}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <SimpleTable<BankStatementItem | BankStatementItemData>
        data={
          (activeTab === "CompanyDeposits"
            ? rowData.usedItems
            : rowData.items) || []
        }
        columns={[
          {
            label: "Name",
            render: (d) => String(d.data?.transaction.description) || "-",
            className: "w-2/3"
          },
          {
            label: "Date",
            render: (d) =>
              d.data?.transaction.date
                ? moment(d.data.transaction.date).format("ll")
                : "-"
          },
          {
            label: "Amount",
            render: (d) => {
              const formattedValue = d.data?.transaction.amount
                ? formatToCurrency({
                    cents: d.data.transaction.amount,
                    currency: d.data.transaction.currency_code
                  })
                : "-";
              const isPositive =
                d.data?.transaction.amount && d.data.transaction.amount >= 0;

              return (
                <p
                  className={cx(
                    isPositive ? "text-success-700" : "text-danger-700"
                  )}
                >
                  {isPositive ? "+ " : "- "}
                  {formattedValue}
                </p>
              );
            },
            className: "text-right"
          }
        ]}
      />

      <div className="h-px w-full bg-alpha-100" />
    </>
  );
};
